import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../../utils/styles';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-1',
    'whitespace-nowrap rounded-lg text-base font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ',
    'disabled:bg-muted disabled:text-white disabled:pointer-events-none disabled:opacity-60',
  ],
  {
    variants: {
      variant: {
        default: 'bg-addition5 text-white hover:bg-[#009DA8]',
        dark: 'bg-primary-text text-white hover:opacity-85',
        destructive: 'bg-destructive text-white hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondaryButton text-secondaryButton-foreground hover:opacity-85 ' +
          'disabled:text-secondaryButton-foreground disabled:opacity-60',
        ghost: 'hover:bg-accent hover:text-accent-foreground disabled:!bg-transparent',
        link: 'text-primary-text underline-offset-4 hover:underline hover:!bg-transparent',
        home: 'text-white bg-[#8794AC] hover:bg-[muted]',
      },
      size: {
        default: 'h-[44px] px-[30px] py-[9px]',
        sm: 'h-[33px] rounded-lg p-[10px] !text-sm',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-[36px] w-[36px] [&_svg_path]:fill-white',
        auto: 'h-auto',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);

Button.displayName = 'Button';

Button.defaultProps = {
  asChild: false,
};

export { Button, buttonVariants };
